import React, { memo, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { AnywhereLoginModal } from 'src/components/Header/LoginModal';
import { HeaderAuthenticatedUserIcon } from 'src/components/Header/UserIcon/AuthenticatedUserIcon';
import { HeaderNonAuthenticatedUserIcon } from 'src/components/Header/UserIcon/NonAuthenticatedUserIcon';
import { IconAIWidget } from 'src/components/Icons/AIWidget';
import { IconShoppingCart } from 'src/components/Icons/ShoppingCart';
import { UiButton } from 'src/components/UI/Button';
import { UserModel } from 'src/models/Account/User';
import { BrandingModelSelectorType } from 'src/models/Branding/BrandingModel';
import { CommonCurrentPartnerType } from 'src/models/CurrentPartner';
import { Enums } from 'src/models/enums';
import { ROUTE_SIGNIN_PATH } from 'src/routes';
import { HeaderHelpComponent } from './Help';
import { HeaderNotifications } from './Notifications';

declare global {
  interface Window {
    onCardChange: () => void;

    cartModel: {
      ShowCartModal: () => void;
      AnyItemAdded: () => boolean;
      TotalItemsQuantity: () => number;
    };

    isKindOfExperiencePage?: boolean;
  }
}

type Props = {
  user: UserModel | null;
  partner: CommonCurrentPartnerType | null;
  brandingModel: BrandingModelSelectorType;
};

export const HeaderNavbarRightItems: React.FC<Props> = memo(({ user, partner, brandingModel }: Props) => {
  const [cardChanged, setCardChanged] = useState<number>(1);

  const { pathname } = useLocation();

  useEffect(() => {
    window.onCardChange = () => {
      setCardChanged(Math.random());
    };
  }, []);

  const showHelp: boolean = useMemo(() => {
    if (user?.IsAuthenticated) {
      /** not show for supporters on campaign pages */
      if (window.isKindOfExperiencePage && user.UserTypeCode === Enums.common.UserTypeCode.Supporter) {
        return false;
      }

      if (partner?.EnableHelp) {
        return true;
      }
    }

    // only show on signin page if enabled on partner
    return !!partner?.EnableHelp && !!matchPath(pathname, ROUTE_SIGNIN_PATH);
  }, [user?.IsAuthenticated, user?.UserTypeCode, pathname, partner?.EnableHelp]);

  const showBeamer = user?.IsAuthenticated && user.canViewBeamerButton && partner && partner.EnableBeamer;


  return (
    <ul className="list-inline pull-right text-center navbar-right-items">
      {window.RallyUp.AIWidgetManager.IsAvailable() && (
        <li className="ai-widget-icon">
          <UiButton className="ai-widget-button BtnSecondaryBordered" 
            onClick={() => window.RallyUp.AIWidgetManager.Toggle()}>
            <IconAIWidget /><div className="ai-widget-icon-text">AI Assistant</div>
          </UiButton>
        </li>
      )}
      {showHelp && <HeaderHelpComponent />}
      {showBeamer && <HeaderNotifications user={user} />}
      {cardChanged && window.cartModel && window.cartModel.AnyItemAdded() && (
        <li className="cart-counter-icon">
          <UiButton tag="a" className="waves-effect" onClick={() => window.cartModel.ShowCartModal()}>
            {window.cartModel.TotalItemsQuantity()}
            <IconShoppingCart />
          </UiButton>
        </li>
      )}
      {user && user.IsAuthenticated ? (
        <HeaderAuthenticatedUserIcon user={user} />
      ) : (
        <HeaderNonAuthenticatedUserIcon partnerName={partner?.Name || null} brandingModel={brandingModel} />
      )}

      <AnywhereLoginModal />
    </ul>
  );
});
